import React, { useState, useEffect } from 'react';

// Simple event-based search store without external dependencies
class SearchEventStore {
  private listeners: ((searchTerm: string, resource: string | null) => void)[] = [];
  private searchTerm = '';
  private currentResource: string | null = null;

  subscribe(listener: (searchTerm: string, resource: string | null) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  setSearch(term: string, resource: string) {
    this.searchTerm = term;
    this.currentResource = resource;
    this.listeners.forEach(listener => listener(term, resource));
  }

  clearSearch() {
    this.searchTerm = '';
    this.currentResource = null;
    this.listeners.forEach(listener => listener('', null));
  }

  getState() {
    return { searchTerm: this.searchTerm, currentResource: this.currentResource };
  }
}

const searchStore = new SearchEventStore();

export function useSearchStore() {
  const [state, setState] = useState(searchStore.getState());

  useEffect(() => {
    return searchStore.subscribe((searchTerm, currentResource) => {
      setState({ searchTerm, currentResource });
    });
  }, []);

  // Create stable references to avoid infinite loops
  const setSearch = React.useCallback((term: string, resource: string) => {
    searchStore.setSearch(term, resource);
  }, []);

  const clearSearch = React.useCallback(() => {
    searchStore.clearSearch();
  }, []);

  return {
    ...state,
    setSearch,
    clearSearch,
  };
}